import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Activity, RefreshCw } from "lucide-react";

const COLORS = {
  blocked: "text-red-400 border-red-500/40 bg-red-500/10",
  allowed: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  logged: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  quarantined: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};
const SEV_COLORS = {
  critical: "text-red-400",
  high: "text-amber-400",
  medium: "text-cyan-400",
  low: "text-zinc-400",
};

export default function Monitor() {
  const [hits, setHits] = useState(null);
  const [recent, setRecent] = useState([]);
  const [windowMin, setWindowMin] = useState(15);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [h, r] = await Promise.all([
        api.get(`/monitor/policy-hits?minutes=${windowMin}`),
        api.get(`/monitor/recent?limit=50`),
      ]);
      setHits(h.data); setRecent(r.data);
    } catch (err) { console.error("monitor load failed", err); }
    finally { setBusy(false); }
  }, [windowMin]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  return (
    <Layout title="Packet Filter Monitor" subtitle="LIVE TELEMETRY">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
          WINDOW {windowMin} MIN · {hits?.total || 0} EVENTS · refresh 5s
        </div>
        <div className="flex items-center gap-2">
          {[5, 15, 60, 240].map((m) => (
            <Button key={m} size="sm" variant={m === windowMin ? "default" : "ghost"}
              data-testid={`window-${m}`} onClick={() => setWindowMin(m)}
              className={"rounded-sm font-mono uppercase text-[10px] tracking-widest " + (m === windowMin ? "bg-cyan-500 text-zinc-950" : "")}>
              {m}m
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} disabled={busy} className="gap-1.5"><RefreshCw className={"w-3.5 h-3.5 " + (busy ? "animate-spin" : "")} />Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="By action" data={hits?.by_action} colorMap={COLORS} testid="panel-action" />
        <Panel title="By severity" data={hits?.by_severity} colorClass={(k) => SEV_COLORS[k] || "text-zinc-400"} testid="panel-severity" />
        <Panel title="By category" data={hits?.by_category} testid="panel-category" />
      </div>

      <div className="border border-zinc-800 rounded-sm bg-zinc-900/60">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">// RECENT EVENTS</div>
        </div>
        <div className="divide-y divide-zinc-800 max-h-[60vh] overflow-y-auto" data-testid="recent-events">
          {recent.map((e) => (
            <div key={e.threat_id} className="px-4 py-2 grid grid-cols-12 gap-2 font-mono text-xs">
              <div className="col-span-2 text-zinc-500 whitespace-normal break-words">{e.timestamp?.slice(11, 19) || "—"}</div>
              <div className={"col-span-1 uppercase " + (SEV_COLORS[e.severity] || "text-zinc-400")}>{e.severity}</div>
              <div className="col-span-2 text-cyan-400 whitespace-normal break-words">{e.category}</div>
              <div className="col-span-2 whitespace-normal break-words">{e.src_ip || e.src}</div>
              <div className="col-span-2 whitespace-normal break-words text-zinc-400">{e.dst_ip || e.dst}:{e.dst_port || e.dport}</div>
              <div className={"col-span-1 " + (COLORS[e.action_taken]?.split(" ")[0] || "text-zinc-400")}>{e.action_taken}</div>
              <div className="col-span-2 text-zinc-500 whitespace-normal break-words">{e.cloud}</div>
            </div>
          ))}
          {recent.length === 0 && <div className="px-4 py-6 text-zinc-500 text-sm">no events.</div>}
        </div>
      </div>
    </Layout>
  );
}

function Panel({ title, data, colorMap, colorClass, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map((e) => e[1]));
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">// {title}</div>
      <div className="space-y-2">
        {entries.length === 0 && <div className="text-zinc-500 font-mono text-xs">no data</div>}
        {entries.map(([k, v]) => {
          const pct = (v / max) * 100;
          const cls = (colorMap && colorMap[k]) || colorClass?.(k) || "text-zinc-400";
          const barCls = colorMap?.[k]?.split(" ")[2] || "bg-cyan-500/40";
          return (
            <div key={k}>
              <div className="flex items-center justify-between font-mono text-xs">
                <span className={"uppercase tracking-widest " + cls}>{k}</span>
                <span className="tabular-nums text-zinc-300">{v}</span>
              </div>
              <div className="h-1 bg-zinc-800 rounded-sm overflow-hidden mt-1">
                <div className={"h-full " + barCls} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
